import {
  clampAxis,
  createEmptyButtons,
  createNeutralCommand,
  type PlayerCommand,
} from "../simulation/commands";
import { BoostBuffer } from "./boost-buffer";

export type Action =
  | "up"
  | "down"
  | "left"
  | "right"
  | "aimUp"
  | "aimDown"
  | "aimLeft"
  | "aimRight"
  | "firePrimary"
  | "fireSecondary"
  | "fireOrdnance"
  | "boost"
  | "interact"
  | "pause"
  | "restart"
  | "mute"
  | "musicToggle"
  | "skill1"
  | "skill2"
  | "skill3";

const keyBindings: Record<string, Action> = {
  ArrowUp: "up",
  KeyW: "up",
  ArrowDown: "down",
  KeyS: "down",
  ArrowLeft: "left",
  KeyA: "left",
  ArrowRight: "right",
  KeyD: "right",
  KeyI: "aimUp",
  KeyK: "aimDown",
  KeyJ: "aimLeft",
  KeyL: "aimRight",
  ShiftLeft: "boost",
  ShiftRight: "boost",
  KeyE: "interact",
  KeyP: "pause",
  KeyR: "restart",
  KeyM: "mute",
  KeyN: "musicToggle",
  Digit1: "skill1",
  Digit2: "skill2",
  Digit3: "skill3",
  KeyQ: "fireSecondary",
  KeyF: "fireOrdnance",
};

type StickState = { x: number; y: number; active: boolean };

export type InputGate = {
  skillPick: boolean;
  menu: boolean;
};

/**
 * Aggregates keyboard, pointer aim, gamepad, and touch sticks into PlayerCommand axes.
 */
export class InputManager {
  private readonly held = new Set<Action>();
  private readonly pressed = new Set<Action>();
  private seq = 0;
  private pointerAim: StickState = { x: 0, y: 0, active: false };
  private firePrimaryPointer = false;
  private touchMove: StickState = { x: 0, y: 0, active: false };
  private touchAim: StickState = { x: 0, y: 0, active: false };
  private touchButtons = createEmptyButtons();
  private readonly touchIds = new Map<number, "move" | "aim">();
  private canvas: HTMLCanvasElement | null = null;
  private inputGate: InputGate = { skillPick: false, menu: false };
  private readonly gamepadPressed = new Set<number>();
  private readonly boostBuffer = new BoostBuffer();

  public constructor(private readonly target: EventTarget = window) {
    target.addEventListener("keydown", this.onKeyDown as EventListener);
    target.addEventListener("keyup", this.onKeyUp as EventListener);
    window.addEventListener("blur", this.clear);
  }

  public attachCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerUp);
    canvas.addEventListener("pointerleave", this.onPointerUp);
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  public isHeld(action: Action): boolean {
    return this.held.has(action);
  }

  public consumePress(action: Action): boolean {
    return this.pressed.delete(action);
  }

  public setTouchMove(x: number, y: number, active: boolean): void {
    this.touchMove = { x: clampAxis(x), y: clampAxis(y), active };
  }

  public setTouchAim(x: number, y: number, active: boolean): void {
    this.touchAim = { x: clampAxis(x), y: clampAxis(y), active };
    if (active) this.firePrimaryPointer = true;
  }

  public setTouchButton<K extends keyof ReturnType<typeof createEmptyButtons>>(
    button: K,
    value: boolean,
  ): void {
    this.touchButtons[button] = value;
    if (value && (button === "pause" || button === "restart" || button === "interact")) {
      this.pressed.add(button);
    }
  }

  private queuedSkillPick = -1;

  public queueSkillPick(index: number): void {
    if (index >= 0 && index <= 2) this.queuedSkillPick = index;
  }

  /** Suppress flight/combat input while skill-pick or menu overlays are open. */
  public setInputGate(gate: InputGate): void {
    this.inputGate = gate;
    if (gate.skillPick || gate.menu) {
      this.touchMove = { x: 0, y: 0, active: false };
      this.touchAim = { x: 0, y: 0, active: false };
      this.firePrimaryPointer = false;
      this.pointerAim.active = false;
      this.touchIds.clear();
    }
  }

  public sampleCommand(tick: number): PlayerCommand {
    const gamepad = this.sampleGamepad();
    const menuOnly = this.inputGate.menu;
    const skillPickOnly = this.inputGate.skillPick && !menuOnly;

    if (menuOnly) {
      const buttons = createEmptyButtons();
      buttons.interact =
        this.consumePress("interact") || this.touchButtons.interact || gamepad.interact;
      buttons.restart = this.consumePress("restart") || this.touchButtons.restart;
      buttons.pause = this.consumePress("pause") || this.touchButtons.pause || gamepad.pause;
      this.touchButtons.interact = false;
      this.touchButtons.restart = false;
      this.touchButtons.pause = false;
      this.seq += 1;
      return { ...createNeutralCommand(this.seq, tick), buttons };
    }

    const moveX = skillPickOnly
      ? 0
      : clampAxis(
          Number(this.held.has("right")) -
            Number(this.held.has("left")) +
            (this.touchMove.active ? this.touchMove.x : 0) +
            gamepad.moveX,
        );
    const moveY = skillPickOnly
      ? 0
      : clampAxis(
          Number(this.held.has("down")) -
            Number(this.held.has("up")) +
            (this.touchMove.active ? this.touchMove.y : 0) +
            gamepad.moveY,
        );

    let aimX = skillPickOnly
      ? 0
      : Number(this.held.has("aimRight")) - Number(this.held.has("aimLeft")) + gamepad.aimX;
    let aimY = skillPickOnly
      ? 0
      : Number(this.held.has("aimDown")) - Number(this.held.has("aimUp")) + gamepad.aimY;
    if (!skillPickOnly) {
      if (this.touchAim.active) {
        aimX += this.touchAim.x;
        aimY += this.touchAim.y;
      } else if (this.pointerAim.active) {
        aimX += this.pointerAim.x;
        aimY += this.pointerAim.y;
      }
    }

    const buttons = createEmptyButtons();
    if (!skillPickOnly) {
      const touchAimFiring =
        this.touchAim.active &&
        (Math.abs(this.touchAim.x) > 0.12 || Math.abs(this.touchAim.y) > 0.12);
      buttons.firePrimary =
        this.held.has("firePrimary") ||
        this.firePrimaryPointer ||
        touchAimFiring ||
        this.touchButtons.firePrimary ||
        gamepad.firePrimary;
      buttons.fireSecondary =
        this.held.has("fireSecondary") || this.touchButtons.fireSecondary || gamepad.fireSecondary;
      buttons.fireOrdnance =
        this.held.has("fireOrdnance") || this.touchButtons.fireOrdnance || gamepad.fireOrdnance;
      buttons.boost = this.boostBuffer.sample(
        this.held.has("boost") || this.touchButtons.boost || gamepad.boost,
      );
      buttons.interact =
        this.consumePress("interact") || this.touchButtons.interact || gamepad.interact;
    }
    buttons.pause = this.consumePress("pause") || this.touchButtons.pause || gamepad.pause;
    buttons.restart = this.consumePress("restart") || this.touchButtons.restart;

    // Edge-trigger touch UI buttons once sampled.
    this.touchButtons.interact = false;
    this.touchButtons.pause = false;
    this.touchButtons.restart = false;

    this.seq += 1;

    let skillPick = -1;
    if (skillPickOnly || this.inputGate.skillPick) {
      if (this.queuedSkillPick >= 0) {
        skillPick = this.queuedSkillPick;
        this.queuedSkillPick = -1;
      } else if (this.consumePress("skill1" as Action) || gamepad.skillPick === 0) skillPick = 0;
      else if (this.consumePress("skill2" as Action) || gamepad.skillPick === 1) skillPick = 1;
      else if (this.consumePress("skill3" as Action) || gamepad.skillPick === 2) skillPick = 2;
    }

    return {
      ...createNeutralCommand(this.seq, tick),
      moveX: clampAxis(moveX),
      moveY: clampAxis(moveY),
      aimX: clampAxis(aimX),
      aimY: clampAxis(aimY),
      skillPick,
      buttons,
    };
  }

  public dispose(): void {
    this.target.removeEventListener("keydown", this.onKeyDown as EventListener);
    this.target.removeEventListener("keyup", this.onKeyUp as EventListener);
    window.removeEventListener("blur", this.clear);
    if (this.canvas) {
      this.canvas.removeEventListener("pointerdown", this.onPointerDown);
      this.canvas.removeEventListener("pointermove", this.onPointerMove);
      this.canvas.removeEventListener("pointerup", this.onPointerUp);
      this.canvas.removeEventListener("pointerleave", this.onPointerUp);
    }
    this.clear();
  }

  private sampleGamepad(): {
    moveX: number;
    moveY: number;
    aimX: number;
    aimY: number;
    firePrimary: boolean;
    fireSecondary: boolean;
    fireOrdnance: boolean;
    boost: boolean;
    interact: boolean;
    pause: boolean;
    skillPick: number;
  } {
    const pads = navigator.getGamepads?.() ?? [];
    const pad = pads.find((p) => p && p.connected);
    if (!pad) {
      return {
        moveX: 0,
        moveY: 0,
        aimX: 0,
        aimY: 0,
        firePrimary: false,
        fireSecondary: false,
        fireOrdnance: false,
        boost: false,
        interact: false,
        pause: false,
        skillPick: -1,
      };
    }
    const dead = (v: number) => (Math.abs(v) < 0.18 ? 0 : v);
    const edge = (index: number) => {
      const down = !!pad.buttons[index]?.pressed;
      const was = this.gamepadPressed.has(index);
      if (down && !was) {
        this.gamepadPressed.add(index);
        return true;
      }
      if (!down) this.gamepadPressed.delete(index);
      return false;
    };

    let skillPick = -1;
    if (this.inputGate.skillPick) {
      // Face buttons: X/Square=1, Y/Triangle=2, B/Circle=3 (A/Cross unused — common confirm)
      if (edge(2)) skillPick = 0;
      else if (edge(3)) skillPick = 1;
      else if (edge(1)) skillPick = 2;
      // D-pad fallback: left/up/right
      else if (edge(14)) skillPick = 0;
      else if (edge(12)) skillPick = 1;
      else if (edge(15)) skillPick = 2;
    }

    return {
      moveX: dead(pad.axes[0] ?? 0),
      moveY: dead(pad.axes[1] ?? 0),
      aimX: dead(pad.axes[2] ?? 0),
      aimY: dead(pad.axes[3] ?? 0),
      firePrimary: !!pad.buttons[7]?.pressed || !!pad.buttons[5]?.pressed,
      fireSecondary: !!pad.buttons[6]?.pressed || !!pad.buttons[4]?.pressed,
      fireOrdnance: !!pad.buttons[1]?.pressed,
      boost: !!pad.buttons[0]?.pressed,
      interact: !!pad.buttons[9]?.pressed,
      pause: edge(8),
      skillPick,
    };
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const action = keyBindings[event.code];
    if (!action) return;
    event.preventDefault();
    if (!event.repeat) this.pressed.add(action);
    this.held.add(action);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const action = keyBindings[event.code];
    if (!action) return;
    event.preventDefault();
    this.held.delete(action);
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (!this.canvas) return;
    if (event.pointerType === "touch") {
      event.preventDefault();
      this.trackTouch(event);
      return;
    }
    if (event.button === 0) {
      this.firePrimaryPointer = true;
      this.held.add("firePrimary");
      this.updatePointerAim(event);
    }
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (event.pointerType === "touch") {
      event.preventDefault();
      this.trackTouch(event);
      return;
    }
    this.updatePointerAim(event);
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (event.pointerType === "touch") {
      event.preventDefault();
      const role = this.touchIds.get(event.pointerId);
      this.touchIds.delete(event.pointerId);
      if (role === "move") this.touchMove = { x: 0, y: 0, active: false };
      if (role === "aim") {
        this.touchAim = { x: 0, y: 0, active: false };
        this.firePrimaryPointer = false;
      }
      return;
    }
    this.firePrimaryPointer = false;
    this.held.delete("firePrimary");
  };

  private trackTouch(event: PointerEvent): void {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const role = this.touchIds.get(event.pointerId) ?? (x < rect.width * 0.5 ? "move" : "aim");
    this.touchIds.set(event.pointerId, role);
    const cx = role === "move" ? rect.width * 0.22 : rect.width * 0.78;
    const cy = rect.height * 0.72;
    const dx = clampAxis((x - cx) / 54);
    const dy = clampAxis((y - cy) / 54);
    if (role === "move") this.touchMove = { x: dx, y: dy, active: true };
    else {
      this.touchAim = { x: dx, y: dy, active: true };
      this.firePrimaryPointer = true;
    }
  }

  private updatePointerAim(event: PointerEvent): void {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    const dx = (event.clientX - (rect.left + rect.width / 2)) / (rect.width * 0.35);
    const dy = (event.clientY - (rect.top + rect.height / 2)) / (rect.height * 0.35);
    this.pointerAim = { x: clampAxis(dx), y: clampAxis(dy), active: true };
  }

  private readonly clear = (): void => {
    this.held.clear();
    this.pressed.clear();
    this.firePrimaryPointer = false;
    this.pointerAim.active = false;
    this.touchMove = { x: 0, y: 0, active: false };
    this.touchAim = { x: 0, y: 0, active: false };
    this.touchButtons = createEmptyButtons();
    this.touchIds.clear();
    this.boostBuffer.clear();
  };
}
